/**
 * The tenant context object itself.
 *
 * It lives in its own module so that `TenantContext.tsx` exports only the provider
 * component: a file exporting both a component and a non-component value defeats
 * React Fast Refresh, which then reloads the whole tree on every edit.
 *
 * The value type is declared here too, so the provider and the consumer hooks agree
 * on one definition rather than two structurally similar ones.
 */

import { createContext } from 'react';

import type { Tenant } from '../api/types';

export interface TenantContextValue {
  /** Currently selected tenant, or null before a selection is made. */
  readonly tenant: Tenant | null;
  readonly tenants: readonly Tenant[];
  readonly selectTenant: (tenantId: string) => void;
  /** True when the user must pick a tenant before business pages are usable. */
  readonly needsSelection: boolean;
}

/**
 * `null` means "no provider above me", which the hooks turn into a loud error.
 * Business pages must never silently run without a tenant scope.
 */
export const TenantContext = createContext<TenantContextValue | null>(null);
